import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { ReactComponent as SearchIconWhite } from '../../../assets/images/search-white.svg'
import { ReactComponent as ListViewUnfillIcon } from '../../../assets/icons/list-view-unfill.svg'
import { ReactComponent as SelectedListViewIcon } from '../../../assets/icons/list-view-filled.svg'
import { ReactComponent as SelectedCardViewIcon } from '../../../assets/icons/grid-view-active.svg'
import { ReactComponent as CollectionUnFillIcon } from '../../../assets/icons/collection-unfill.svg'
import { isPageReachBottom } from '../../helpers/Util'
import { closeLoader, openLoader } from '../../redux_toolkit/Loader'
import OrderThunks from '../../redux_toolkit/Order/OrderThunks'
import { GET_ORDER_LIST } from '../../redux_toolkit/@types/thunk.types'
import { useOrderSelector } from '../../redux_toolkit/Order'
import ListOfOrder from './ListOfOrder'
import OrderTableView from './OrderTableView'
import { useLoader } from '../../redux_toolkit/Loader'
import { addMixpanelTrackingEvent } from '../../services/CommonService'
import {
    setDashboardViewType,
    useDashboardViewSelector
} from '../../redux_toolkit/Dashboard/DashboardViewSlice'

const MyOrderList = ({ total, dateRange }) => {
    const orderStore = useOrderSelector()
    const dispatch = useDispatch()
    const myStateRef = useRef({})
    const dateRangeRef = useRef(dateRange)
    const [search, setSearch] = useState('')
    const { viewType } = useDashboardViewSelector()
    const globalLoader = useLoader()
    const isLoading = useRef(null)
    const navigate = useNavigate()
    const location = useLocation()
    const activeStatusParam = new URLSearchParams(location.search).get('status')
    const [activeTab, setActiveTab] = useState(activeStatusParam || 'ALL')

    const setIsDataLoading = (data) => {
        isLoading.current = data
    }

    useEffect(() => {
        setIsDataLoading(globalLoader)
    }, [globalLoader])

    useEffect(() => {
        if (orderStore) {
            myStateRef.current = { ...myStateRef.current, ...orderStore }
            setActiveTab(orderStore?.activeTab)
        }
    }, [orderStore])

    const getTabName = {
        ALL: 'RUNNING,COMPLETED,ON_SHIPMENT,PENDING,INITIALIZED,CANCELLED',
        COMPLETED: 'COMPLETED,ON_SHIPMENT',
        PENDING: 'PENDING,INITIALIZED',
        RUNNING: 'RUNNING',
        CANCELLED: 'CANCELLED'
    }

    const generateParams = (tabName, page, searchParam) => {
        let tab = getTabName[tabName]
        if (tab !== getTabName['CANCELLED']) {
            let params = `?size=16&sort=id,desc&statusList=${tab}&page=${page}`
            if (searchParam) {
                params += `&search=${searchParam}&poNumber=${searchParam}`
            }
            return params
        } else {
            let params = `?size=16&sort=id,desc&statusList=INITIALIZED&approvalStatus=${tab}&page=${page}`
            if (searchParam) {
                params += `&search=${searchParam}&poNumber=${searchParam}`
            }
            return params
        }
    }

    useEffect(() => {
        dispatch(openLoader())
        const initialTab = activeStatusParam || 'ALL'
        let params = generateParams(initialTab, 0)
        dispatch(
            OrderThunks[GET_ORDER_LIST](params, false, initialTab, dateRange)
        ).finally(() => dispatch(closeLoader()))
        document.addEventListener('scroll', handleScroll)
        return () => {
            document.removeEventListener('scroll', handleScroll)
        }
    }, [])

    useEffect(() => {
        dateRangeRef.current = dateRange
        dispatch(openLoader())
        const currentTab =
            myStateRef.current?.activeTab || activeStatusParam || 'ALL'
        let params = generateParams(currentTab, 0)
        dispatch(
            OrderThunks[GET_ORDER_LIST](params, false, currentTab, dateRange)
        ).finally(() => dispatch(closeLoader()))
    }, [dateRange?.type, dateRange?.fromDate, dateRange?.toDate])

    const handleScroll = () => {
        if (isPageReachBottom()) {
            let { totalElements, currentPage, data } =
                myStateRef.current.orderResponse
            let activeTabName = myStateRef.current.activeTab
            if (totalElements > 0) {
                if (totalElements !== data.length && !isLoading.current) {
                    dispatch(openLoader())
                    let params = generateParams(
                        activeTabName,
                        currentPage + 1,
                        myStateRef.current.searchParam
                    )
                    dispatch(
                        OrderThunks[GET_ORDER_LIST](
                            params,
                            true,
                            activeTabName,
                            dateRangeRef.current
                        )
                    ).finally(() => dispatch(closeLoader()))
                }
            }
        }
    }

    const changeTab = (value) => {
        window.scrollTo(0, 0)
        dispatch(openLoader())
        let params = generateParams(value, 0, myStateRef.current.searchParam)
        dispatch(
            OrderThunks[GET_ORDER_LIST](params, false, value, dateRange)
        ).finally(() => dispatch(closeLoader()))
        navigate(`/dashboard?active=ORDER&status=${value}`)
        const requestBody = {
            requestTab: 'ORDER',
            status: value
        }
        addMixpanelTrackingEvent(requestBody, 'BUYER_DASHBOARD_CHANGE_TAB')
    }

    const handleSearch = (e) => {
        setSearch(e.target.value)
        myStateRef.current = { ...orderStore, searchParam: e.target.value }
        changeTab(activeTab)
    }

    const renderEmptyState = () => {
        if (orderStore?.orderResponse?.data?.length === 0) {
            return (
                <div className='w-full'>
                    <div className='py-40 text-center max-w-[600px] m-auto'>
                        <h1 className='regular-20 weight-700 gray_400 mb-2'>
                            No order found
                        </h1>
                    </div>
                </div>
            )
        }
    }

    const allTotal =
        (total?.order?.RUNNING ?? 0) +
        (total?.order?.PENDING ?? 0) +
        (total?.order?.COMPLETED ?? 0) +
        (total?.order?.CANCELLED ?? 0)

    return (
        <>
            <div className='order-list-page-container'>
                <div className='tab-and-search flex items-center justify-between'>
                    <div>
                        <ul className='common-tab on-order'>
                            <li
                                className={activeTab === 'ALL' ? 'active' : ''}
                                onClick={() => changeTab('ALL')}
                            >
                                <span className='task-name regular-14'>
                                    All ({allTotal})
                                </span>
                            </li>
                            <li
                                className={
                                    activeTab === 'RUNNING' ? 'active' : ''
                                }
                                onClick={() => changeTab('RUNNING')}
                            >
                                <span className='task-name regular-14'>
                                    Running ({total?.order?.RUNNING})
                                </span>
                            </li>
                            <li
                                className={
                                    activeTab === 'PENDING' ? 'active' : ''
                                }
                                onClick={() => changeTab('PENDING')}
                            >
                                <span className='task-name regular-14'>
                                    Pending ({total?.order?.PENDING})
                                </span>
                            </li>
                            <li
                                className={
                                    activeTab === 'COMPLETED' ? 'active' : ''
                                }
                                onClick={() => changeTab('COMPLETED')}
                            >
                                <span className='task-name regular-14'>
                                    Completed ({total?.order?.COMPLETED})
                                </span>
                            </li>
                            <li
                                className={
                                    activeTab === 'CANCELLED' ? 'active' : ''
                                }
                                onClick={() => changeTab('CANCELLED')}
                            >
                                <span className='task-name regular-14'>
                                    Cancelled ({total?.order?.CANCELLED})
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div className='order-view-toggle flex items-center gap-1'>
                        <button
                            className='button !p-0 min-w-[36px] min-h-[36px] flex items-center justify-center'
                            onClick={() =>
                                dispatch(setDashboardViewType('CARD_VIEW'))
                            }
                            title='Card view'
                        >
                            {viewType === 'CARD_VIEW' ? (
                                <SelectedCardViewIcon className='!w-[36px] !h-[36px]' />
                            ) : (
                                <CollectionUnFillIcon className='!w-[20px] !h-[20px]' />
                            )}
                        </button>
                        <button
                            className='button !p-0 min-w-[36px] min-h-[36px] flex items-center justify-center'
                            onClick={() =>
                                dispatch(setDashboardViewType('TABLE_VIEW'))
                            }
                            title='Table view'
                        >
                            {viewType === 'TABLE_VIEW' ? (
                                <SelectedListViewIcon className='!w-[36px] !h-[36px]' />
                            ) : (
                                <ListViewUnfillIcon className='!w-[20px] !h-[20px]' />
                            )}
                        </button>
                    </div>
                </div>

                {viewType === 'TABLE_VIEW' ? (
                    <OrderTableView
                        orderStore={orderStore}
                        activeTab={activeTab}
                    />
                ) : (
                    <ListOfOrder
                        orderStore={orderStore}
                        activeTab={activeTab}
                    />
                )}
                {renderEmptyState()}
            </div>
        </>
    )
}

export default MyOrderList
